import React, { useState, useEffect } from 'react';
import { HistoricalEvent, CreateEventData, UpdateEventData } from '../../services/timeTunnelService';
import { timeTunnelService } from '../../services/timeTunnelService';
import './TimeTunnel.css';

interface EventFormProps {
  event: HistoricalEvent | null;
  countries: string[];
  onSave: () => void;
  onCancel: () => void;
}

const EventForm: React.FC<EventFormProps> = ({ event, countries, onSave, onCancel }) => {
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [month, setMonth] = useState<number | null>(null);
  const [day, setDay] = useState<number | null>(null);
  const [country, setCountry] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (event) {
      setYear(event.year);
      setMonth(event.month);
      setDay(event.day);
      setCountry(event.country);
      setTitle(event.title);
      setDescription(event.description || '');
    }
  }, [event]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!year || !country || !title.trim()) {
      setError('Year, country, and title are required');
      return;
    }

    if (month !== null && (month < 1 || month > 12)) {
      setError('Month must be between 1 and 12');
      return;
    }

    if (day !== null && (day < 1 || day > 31)) {
      setError('Day must be between 1 and 31');
      return;
    }

    setSaving(true);
    try {
      if (event) {
        // Update existing event
        const updateData: UpdateEventData = {
          year,
          month: month || null,
          day: day || null,
          country,
          title: title.trim(),
          description: description.trim() || null
        };
        await timeTunnelService.updateEvent(event.id, updateData);
      } else {
        // Create new event
        const createData: CreateEventData = {
          year,
          month: month || null,
          day: day || null,
          country,
          title: title.trim(),
          description: description.trim() || null
        };
        await timeTunnelService.createEvent(createData);
      }
      onSave();
    } catch (err: any) {
      console.error('Error saving event:', err);
      setError(err.message || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="event-form-overlay" onClick={onCancel}>
      <div className="event-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="event-form-header">
          <h2>{event ? 'Edit Event' : 'Add Event'}</h2>
          <button onClick={onCancel} className="event-form-close">×</button>
        </div>

        <form onSubmit={handleSubmit} className="event-form">
          {error && <div className="event-form-error">{error}</div>}

          <div className="event-form-group">
            <label>Year *</label>
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value) || 0)}
              required
              min="-10000"
              max="10000"
              className="event-form-input"
            />
          </div>

          <div className="event-form-group">
            <label>Month</label>
            <select
              value={month || ''}
              onChange={(e) => setMonth(e.target.value ? parseInt(e.target.value) : null)}
              className="event-form-select"
            >
              <option value="">None</option>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                <option key={m} value={m}>
                  {new Date(2000, m - 1).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>

          {month && (
            <div className="event-form-group">
              <label>Day</label>
              <select
                value={day || ''}
                onChange={(e) => setDay(e.target.value ? parseInt(e.target.value) : null)}
                className="event-form-select"
              >
                <option value="">None</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          )}

          <div className="event-form-group">
            <label>Country/Place *</label>
            <input
              type="text"
              list="countries-list"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              required
              className="event-form-input"
              placeholder="Enter country or place name"
            />
            <datalist id="countries-list">
              {countries.map(c => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>

          <div className="event-form-group">
            <label>Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={500}
              className="event-form-input"
              placeholder="Event title"
            />
          </div>

          <div className="event-form-group">
            <label>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              className="event-form-textarea"
              placeholder="Event description"
            />
          </div>

          <div className="event-form-actions">
            <button type="button" onClick={onCancel} className="event-form-cancel-btn">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="event-form-save-btn">
              {saving ? 'Saving...' : event ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventForm;

